import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import { fmtL, fmtNum, fmtDate, apiErr } from "@/lib/format";
import {
  CurrencyDollarIcon, ReceiptIcon, UsersThreeIcon, LightbulbIcon, FlagIcon, FileTextIcon, ArrowRightIcon,
  TargetIcon, WarningCircleIcon, PackageIcon, MapPinIcon,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell,
} from "recharts";

/**
 * Dashboard comercial (Delegados y RTC): cómo van las ventas sin información financiera reservada
 * (sin cuentas por cobrar, sin valoración de inventario, sin costos ni utilidad).
 * Delegado: todas las regiones · RTC: solo sus regiones asignadas (lo filtra el servidor).
 */
const TT = { background: "#111612", border: "1px solid #222D26", borderRadius: 8, fontSize: 12 };
const k1000 = (v) => (v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v);

function Variacion({ pct, txt }) {
  if (pct === null || pct === undefined) return <span className="text-text-muted">{txt}: sin datos para comparar</span>;
  const up = pct >= 0;
  return <span className={up ? "text-agro-green" : "text-red-400"}>{up ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}% {txt}</span>;
}

export default function ComercialDashboard() {
  const navigate = useNavigate();
  const [k, setK] = useState(null);
  const [error, setError] = useState("");
  const [periodo, setPeriodo] = useState("mes");          // período de regiones y top: mes | mes_anterior
  const [ops, setOps] = useState(null);                   // oportunidades sugeridas del mes
  const [region, setRegion] = useState("");               // RTC con varias regiones: una o el consolidado ("")
  const [periodoGestion, setPeriodoGestion] = useState("mes");
  const [gestion, setGestion] = useState(null);           // RTC: metas, clientes, CxC e inventario
  useEffect(() => {
    api.get("/dashboard/comercial", { params: { periodo, region } }).then(({ data }) => setK(data))
      .catch((e) => setError(apiErr(e) || "No se pudieron cargar los indicadores"));
  }, [periodo, region]);
  useEffect(() => {
    setOps(null);
    api.get("/dashboard/comercial/oportunidades", { params: { region } }).then(({ data }) => setOps(data)).catch(() => setOps({ activas: 0, top: [], error: true }));
  }, [region]);
  useEffect(() => {
    api.get("/dashboard/comercial/rtc-resumen", { params: { periodo: periodoGestion, region } }).then(({ data }) => setGestion(data)).catch(() => setGestion(null));
  }, [periodoGestion, region]);
  const esRtc = Boolean(gestion?.aplica);
  const txtPeriodo = periodo === "mes_anterior" ? "del mes anterior" : "del mes";
  const Selector = () => (
    <div className="inline-flex border border-border rounded-md overflow-hidden text-[11px]" data-testid="dash-periodo">
      {[["mes", "Mes"], ["mes_anterior", "Mes anterior"]].map(([v, l]) => (
        <button key={v} onClick={() => setPeriodo(v)} data-testid={`dash-periodo-${v}`}
          className={`px-2.5 py-1 ${periodo === v ? "bg-agro-green/15 text-agro-green" : "text-text-secondary hover:text-text-primary"}`}>{l}</button>
      ))}
    </div>
  );
  if (error) return <div className="text-sm text-red-400">{error}</div>;
  if (gestion?.sin_regiones) {
    return (
      <div className="bg-agro-yellow/5 border border-agro-yellow/40 rounded-2xl p-6 text-sm" data-testid="dash-sin-regiones">
        <div className="flex items-center gap-2 font-semibold text-agro-yellow"><WarningCircleIcon size={18} /> Faltan tus regiones</div>
        <p className="text-text-secondary mt-2">{gestion.mensaje}</p>
      </div>
    );
  }
  if (!k) return <div className="text-text-muted">Cargando indicadores…</div>;
  const asignadas = k.regiones_asignadas || gestion?.regiones_asignadas || [];

  return (
    <div className="space-y-5" data-testid="dashboard-comercial">
      <div className="flex flex-wrap items-center gap-3 text-xs text-text-secondary" data-testid="dash-alcance">
        {k.alcance === "todas"
          ? <span>Mostrando <b className="text-text-primary">todas las regiones</b>.</span>
          : <span>Mostrando {region ? <>la región <b className="text-agro-green">{region}</b></> : <>tus regiones asignadas: <b className="text-agro-green">{asignadas.join(", ") || "ninguna"}</b></>}.</span>}
        {asignadas.length > 1 && (
          <div className="inline-flex border border-border rounded-md overflow-hidden" data-testid="dash-region">
            <span className="px-2 py-1 text-text-muted flex items-center gap-1"><MapPinIcon size={12} /> Región</span>
            {["", ...asignadas].map((r) => (
              <button key={r || "todas"} onClick={() => setRegion(r)} data-testid={`dash-region-${r || "todas"}`}
                className={`px-2.5 py-1 ${region === r ? "bg-agro-green/15 text-agro-green" : "hover:text-text-primary"}`}>{r || "Todas mis regiones"}</button>
            ))}
          </div>
        )}
      </div>

      {esRtc && <MiGestion g={gestion} periodo={periodoGestion} setPeriodo={setPeriodoGestion} navigate={navigate} />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-ventas-dia" label="Ventas del día" value={fmtL(k.ventas_dia)} sub={`${k.facturas_dia} facturas hoy`}
          icon={CurrencyDollarIcon} accent="green" trend={null} />
        <KpiCard testId="kpi-ventas-mes" label="Ventas del mes" value={fmtL(k.ventas_mes)}
          sub={<Variacion pct={k.variacion_mes_pct} txt="vs. mismo período del mes anterior" />} icon={ReceiptIcon} accent="yellow" trend={null} />
        <button type="button" onClick={() => navigate("/calendario")} className="text-left" data-testid="kpi-oportunidades">
          <KpiCard label="Oportunidades del mes" value={ops ? fmtNum(ops.activas) : "…"}
            sub={ops ? `${fmtL(ops.valor_estimado || 0)} estimado · ${ops.atrasadas || 0} atrasadas` : "Calculando…"}
            icon={LightbulbIcon} accent="blue" trend={null} />
        </button>
        <KpiCard testId="kpi-ticket" label="Ticket promedio del mes" value={fmtL(k.ticket_promedio_mes)}
          sub={`${k.facturas_mes} facturas · ${k.clientes_compraron_mes} clientes`} icon={UsersThreeIcon} accent="green" trend={null} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button onClick={() => navigate("/clientes")} className="text-left bg-card border border-border/60 rounded-2xl p-5 hover:border-agro-green/40" data-testid="dash-clientes">
          <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Clientes</div>
          <div className="font-heading text-2xl mt-1">{fmtNum(k.clientes_compraron_mes)} <span className="text-sm text-text-muted">de {fmtNum(k.clientes_total)}</span></div>
          <div className="text-xs text-text-secondary mt-1">compraron este mes</div>
        </button>
        <button onClick={() => navigate("/calendario")} className="text-left bg-card border border-border/60 rounded-2xl p-5 hover:border-blue-400/40" data-testid="dash-por-activar">
          <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted flex items-center gap-1"><FlagIcon size={12} /> Por activar</div>
          <div className="font-heading text-2xl mt-1 text-blue-300">{fmtNum(k.clientes_por_activar)}</div>
          <div className="text-xs text-text-secondary mt-1">sin compras en 60+ días o nunca · ver en Calendario</div>
        </button>
        <button onClick={() => navigate("/ventas")} className="text-left bg-card border border-border/60 rounded-2xl p-5 hover:border-agro-yellow/40" data-testid="dash-proformas">
          <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted flex items-center gap-1"><FileTextIcon size={12} /> Proformas</div>
          <div className="font-heading text-2xl mt-1 text-agro-yellow">{fmtNum(k.proformas?.pendiente || 0)}</div>
          <div className="text-xs text-text-secondary mt-1">pendientes de aprobación · {k.proformas?.devuelta || 0} devueltas · {k.proformas?.aprobada || 0} aprobadas</div>
        </button>
      </div>

      <Oportunidades ops={ops} onVer={() => navigate("/calendario")} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-5 bg-card border border-border/60 rounded-2xl p-5" data-testid="chart-ventas-6-meses">
          <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Tendencia</div>
          <h3 className="font-heading text-lg font-medium mt-1 mb-4">Ventas últimos 6 meses</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={k.ventas_6_meses} margin={{ left: -10, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222D26" vertical={false} />
              <XAxis dataKey="mes" stroke="#68756D" fontSize={11} />
              <YAxis stroke="#68756D" fontSize={11} tickFormatter={k1000} />
              <Tooltip contentStyle={TT} formatter={(v) => [fmtL(v), "Ventas"]} />
              <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                {k.ventas_6_meses.map((_, i) => <Cell key={i} fill={i === k.ventas_6_meses.length - 1 ? "#02BF44" : "rgba(2,191,68,0.45)"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="lg:col-span-4 bg-card border border-border/60 rounded-2xl p-5" data-testid="chart-ventas-7-dias">
          <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Últimos 7 días</div>
          <h3 className="font-heading text-lg font-medium mt-1 mb-4">Ventas diarias</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={k.ventas_7_dias} margin={{ left: -10, right: 8, top: 8 }}>
              <defs><linearGradient id="gradc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#02BF44" stopOpacity={0.55} /><stop offset="100%" stopColor="#02BF44" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#222D26" vertical={false} />
              <XAxis dataKey="fecha" stroke="#68756D" fontSize={11} tickFormatter={(d) => fmtDate(d).split(" de ").slice(0, 2).join(" ")} />
              <YAxis stroke="#68756D" fontSize={11} tickFormatter={k1000} />
              <Tooltip contentStyle={TT} formatter={(v) => [fmtL(v), "Ventas"]} labelFormatter={(d) => fmtDate(d)} />
              <Area type="monotone" dataKey="total" stroke="#02BF44" strokeWidth={2.4} fill="url(#gradc)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="lg:col-span-3 bg-card border border-border/60 rounded-2xl p-5" data-testid="chart-region">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{periodo === "mes_anterior" ? "Mes anterior" : "Este mes"}</div>
            <Selector />
          </div>
          <h3 className="font-heading text-lg font-medium mt-1 mb-3">Ventas por región</h3>
          {k.ventas_por_region.length === 0 ? <div className="text-xs text-text-muted">Sin ventas {txtPeriodo}.</div> : (
            <div className="space-y-2">
              {k.ventas_por_region.slice(0, 8).map((r) => {
                const max = k.ventas_por_region[0].total || 1;
                return (
                  <div key={r._id}>
                    <div className="flex justify-between text-xs"><span>{r._id}</span><span className="font-mono">{fmtL(r.total)}</span></div>
                    <div className="h-1.5 bg-app rounded mt-1"><div className="h-1.5 bg-agro-green rounded" style={{ width: `${Math.max(4, (r.total / max) * 100)}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Top titulo={`Clientes ${txtPeriodo}`} testid="top-clientes" selector={<Selector />} vacio={`Sin ventas ${txtPeriodo}.`} filas={k.top_clientes.map((c) => [c.nombre, `${c.facturas} fact.`, fmtL(c.total)])} />
        <Top titulo={`Productos ${txtPeriodo}`} testid="top-productos" selector={<Selector />} vacio={`Sin ventas ${txtPeriodo}.`} filas={k.top_productos.map((p) => [p.descripcion, `${fmtNum(p.cantidad)} unid.`, fmtL(p.total)])} />
      </div>
      {!esRtc && <button onClick={() => navigate("/reportes")} className="text-xs text-agro-green inline-flex items-center gap-1">Ver reportes detallados <ArrowRightIcon size={12} /></button>}
    </div>
  );
}

// RTC: indicadores para evaluar su avance y priorizar (solo registros reales de sus regiones; cada uno lleva al detalle)
function MiGestion({ g, periodo, setPeriodo, navigate }) {
  const PER = [["mes", "Este mes"], ["mes_anterior", "Mes anterior"], ["trimestre", "3 meses"], ["anio", "Este año"]];
  const c = g.cxc || {};
  const Tarjeta = ({ testid, onClick, titulo, icono: Icono, color, children }) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className="text-left bg-card border border-border/60 rounded-2xl p-5 hover:border-agro-green/40" data-color={color}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted flex items-center gap-1"><Icono size={12} /> {titulo}</div>
      {children}
    </button>
  );
  return (
    <div className="space-y-3" data-testid="dash-mi-gestion">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-lg">Mi gestión <span className="text-xs text-text-muted font-normal">· {g.periodo_txt} ({fmtDate(g.desde)} – {fmtDate(g.hasta)})</span></h3>
        <div className="inline-flex border border-border rounded-md overflow-hidden text-[11px]" data-testid="dash-gestion-periodo">
          {PER.map(([v, l]) => (
            <button key={v} onClick={() => setPeriodo(v)} data-testid={`dash-gestion-periodo-${v}`}
              className={`px-2.5 py-1 ${periodo === v ? "bg-agro-green/15 text-agro-green" : "text-text-secondary hover:text-text-primary"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tarjeta testid="dash-meta" onClick={() => navigate("/ventas")} titulo="Ventas vs. meta" icono={TargetIcon} color="agro-green">
          <div className="font-heading text-2xl mt-1">{fmtL(g.ventas?.total)}</div>
          <div className="text-xs text-text-secondary mt-1">{g.ventas?.facturas || 0} facturas · {g.ventas?.clientes || 0} clientes</div>
          {g.meta?.monto > 0 ? (
            <>
              <div className="h-1.5 bg-app rounded mt-3"><div className="h-1.5 bg-agro-green rounded" style={{ width: `${Math.min(100, g.meta.avance_pct || 0)}%` }} /></div>
              <div className="text-xs mt-1"><b className="text-agro-green">{g.meta.avance_pct}%</b> de {fmtL(g.meta.monto)} {g.meta.origen === "rtc" ? "(tu meta)" : "(meta de tus regiones)"}</div>
            </>
          ) : <div className="text-xs text-text-muted mt-3" data-testid="dash-meta-vacia">Sin metas configuradas para este período.</div>}
        </Tarjeta>
        <Tarjeta testid="dash-clientes-atendidos" onClick={() => navigate("/clientes")} titulo="Clientes atendidos" icono={UsersThreeIcon} color="agro-green">
          <div className="font-heading text-2xl mt-1">{fmtNum(g.clientes?.atendidos)} <span className="text-sm text-text-muted">de {fmtNum(g.clientes?.total)}</span></div>
          <div className="text-xs text-text-secondary mt-1">con venta, proforma, cobro o gestión en el período</div>
        </Tarjeta>
        <Tarjeta testid="dash-cxc" onClick={() => navigate("/cxc")} titulo="Cuentas por cobrar" icono={ReceiptIcon} color="agro-yellow">
          <div className="font-heading text-2xl mt-1 text-agro-yellow">{fmtL(c.pendiente)}</div>
          <div className="text-xs text-text-secondary mt-1">{c.documentos || 0} documentos · {c.clientes || 0} clientes</div>
          <div className="text-xs mt-2 space-y-0.5">
            <div className="flex justify-between"><span className="text-red-400">Vencido</span><span>{fmtL(c.vencido)} · {c.vencidos || 0}</span></div>
            <div className="flex justify-between"><span>Vence en 7 días</span><span>{fmtL(c.por_vencer_7)} · {c.n_7 || 0}</span></div>
            <div className="flex justify-between"><span>Vence en 8–30 días</span><span>{fmtL(c.por_vencer_30)} · {c.n_30 || 0}</span></div>
          </div>
        </Tarjeta>
        <Tarjeta testid="dash-seguimiento" onClick={() => navigate("/cxc")} titulo="Pendientes de seguimiento" icono={WarningCircleIcon} color="red-400">
          <div className="font-heading text-2xl mt-1 text-red-300">{fmtNum(g.clientes?.pendientes_seguimiento)}</div>
          <div className="text-xs text-text-secondary mt-1">con saldo vencido y sin cobro ni gestión en 30 días</div>
          {(g.clientes?.seguimiento_top || []).slice(0, 3).map((x) => <div key={x.cliente_id} className="text-[11px] text-text-muted truncate">· {x.nombre}</div>)}
        </Tarjeta>
      </div>
      <button type="button" onClick={() => navigate("/productos/lista-precios")} data-testid="dash-inventario"
        className="w-full text-left bg-card border border-border/60 rounded-2xl p-4 hover:border-agro-green/40 flex items-center gap-3 text-sm">
        <PackageIcon size={18} className="text-agro-green" />
        <span><b>{fmtNum(g.inventario?.productos_con_existencia)}</b> productos con existencia {g.inventario?.ubicaciones ? `en tus ${g.inventario.ubicaciones} ubicaciones` : "disponibles para la venta"} · ver lista de precios</span>
        <ArrowRightIcon size={14} className="ml-auto text-text-muted" />
      </button>
    </div>
  );
}

function Top({ titulo, filas, testid, selector, vacio }) {
  return (
    <div className="bg-card border border-border/60 rounded-2xl overflow-hidden" data-testid={testid}>
      <div className="p-5 border-b border-border/60 flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Top 5</div>
          <h3 className="font-heading text-lg font-medium mt-1">{titulo}</h3>
        </div>
        {selector}
      </div>
      {filas.length === 0 ? <div className="p-5 text-xs text-text-muted">{vacio}</div> : (
        <table className="w-full text-sm">
          <tbody>
            {filas.map((f, i) => (
              <tr key={i} className="border-t border-border/30">
                <td className="p-3 pl-5 truncate max-w-[18rem]">{f[0]}</td>
                <td className="p-3 text-right text-xs text-text-muted">{f[1]}</td>
                <td className="p-3 pr-5 text-right font-mono">{f[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const PRIO = { ALTA: "border-red-500/40 text-red-300", MEDIA: "border-agro-yellow/40 text-agro-yellow", BAJA: "border-border text-text-secondary" };

function Oportunidades({ ops, onVer }) {
  if (!ops) return <div className="bg-card border border-border/60 rounded-2xl p-5 text-xs text-text-muted">Calculando oportunidades del mes…</div>;
  return (
    <div className="bg-card border border-blue-500/30 rounded-2xl overflow-hidden" data-testid="dash-oportunidades">
      <div className="p-5 border-b border-border/60 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-blue-300 flex items-center gap-1"><LightbulbIcon size={12} /> Oportunidades sugeridas del mes</div>
          <h3 className="font-heading text-lg font-medium mt-1">Qué ofrecer y a quién</h3>
          <div className="text-xs text-text-secondary mt-1">
            {fmtNum(ops.activas)} activas en {fmtNum(ops.clientes || 0)} clientes · valor estimado {fmtL(ops.valor_estimado || 0)}
            · {ops.proximos_7_dias || 0} para contactar en los próximos 7 días · {ops.gestionadas || 0} gestionadas
          </div>
        </div>
        <div className="flex gap-2 text-xs">
          {["ALTA", "MEDIA", "BAJA"].map((p) => (
            <span key={p} className={`px-2 py-1 rounded border ${PRIO[p]}`}>{p}: {ops.por_prioridad?.[p] || 0}</span>
          ))}
          {ops.atrasadas > 0 && <span className="px-2 py-1 rounded border border-red-500/40 text-red-300">Atrasadas: {ops.atrasadas}</span>}
        </div>
      </div>
      {ops.top.length === 0 ? (
        <div className="p-5 text-xs text-text-muted">{ops.error ? "No se pudieron calcular las oportunidades." : "Sin oportunidades activas para este mes."}</div>
      ) : (
        <table className="w-full text-sm" data-testid="dash-oportunidades-top">
          <thead className="text-[10px] uppercase tracking-wider text-text-muted">
            <tr className="text-left"><th className="p-3 pl-5">Prioridad</th><th className="p-3">Cliente</th><th className="p-3">Producto</th>
              <th className="p-3">Cantidad sugerida</th><th className="p-3">Contactar</th><th className="p-3 pr-5 text-right">Valor estimado</th></tr>
          </thead>
          <tbody>
            {ops.top.map((o, i) => (
              <tr key={i} className="border-t border-border/30">
                <td className="p-3 pl-5"><span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIO[o.prioridad]}`}>{o.prioridad}</span></td>
                <td className="p-3"><div className="truncate max-w-[16rem]">{o.cliente}</div><div className="text-[10px] text-text-muted">{o.region}</div></td>
                <td className="p-3 truncate max-w-[16rem]">{o.producto}</td>
                <td className="p-3 text-xs">{o.cantidad_texto}</td>
                <td className={`p-3 text-xs whitespace-nowrap ${o.atrasada ? "text-red-300" : ""}`}>{fmtDate(o.fecha_sugerida)}{o.atrasada ? " · atrasada" : ""}</td>
                <td className="p-3 pr-5 text-right font-mono">{o.valor_estimado ? fmtL(o.valor_estimado) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="p-3 px-5 border-t border-border/40 flex justify-between items-center">
        <span className="text-[10px] text-text-muted">Estimaciones a partir de compras anteriores (temporada y reposición); no son pedidos confirmados.</span>
        <button onClick={onVer} className="text-xs text-agro-green inline-flex items-center gap-1">Gestionar en el Calendario <ArrowRightIcon size={12} /></button>
      </div>
    </div>
  );
}
